import React from 'react';
import { getString } from '@nackle/intl-tools';
import { Result as AntResult } from 'antd';
import { InfoFilled, CircleCheckFilled, ErrorFilled, CircleCloseFilled, DangerFilled } from '@nackle/origami-icons';
import { NackleIllustration } from '../NackleIllustration/nackle-illustration';
import classnames from 'classnames';

const { PageNotFound, FailedLoad, AllCaughtUp, NoReportsSelected } = NackleIllustration;

/**
 * Possible status values for the Result component
 * @typedef {'success' | 'error' | 'info' | 'warning' | '404' | '403' | '500'} ResultStatus
 */
/**
 * Returns the appropriate image component based on the status
 * @param {ResultStatus} status - The status of the result
 * @param {string} title - The title of the result
 * @returns {React.ReactElement} The image component to display
 */
const getImage = ( status, title ) => {
    if ( status === 'success' ) {
        return <AllCaughtUp className="result-image" aria-label={ getString( 'origami.result.success' ) } />;
    }
    if ( status === 'warning' || status === '500' || status === 'error' ) {
        return <FailedLoad className="result-image" aria-label={ getString( 'origami.result.error' ) } />;
    }
    if ( status === '404' || status === '403' ) {
        return <PageNotFound className="result-image" aria-label={ getString( 'origami.result.404' ) } />;
    }
    return <NoReportsSelected className="result-image" aria-label={ getString( 'origami.result.default' ) } />;
};

/**
 * Returns the appropriate icon component based on the status
 * @param {ResultStatus} status - The status of the result
 * @returns {React.ReactElement} The icon component to display
 */
const getIcon = ( status ) => {
    if ( status === 'success' ) {
        return <CircleCheckFilled />;
    }
    // the figma matches the default icon for warning
    if ( status === 'warning' ) {
        return <DangerFilled />;
    }
    if ( status === 'error' || status === '500' ) {
        return <CircleCloseFilled />;
    }
    if ( status === '404' || status === '403' ) {
        return <ErrorFilled />;
    }
    return <InfoFilled />;
};

/**
 * Result component that wraps Ant Design's Result component with custom styling and icons
 *
 * {@link https://ant.design/components/result#api}
 * 
 * @param {Object} props - The component props
 * @param {ResultStatus} [props.status] - The status of the result
 * @param {'image' | 'icon'} [props.type] - The type of visual to display (image or icon)
 * @param {string} [props.title] - The title text
 * @param {'small' | 'default'} [props.size] - The size of the result component
 * @param {boolean} [props.hug] - whether to remove the padding from the result
 * @param {React.ReactNode} children - The child elements
 * @returns {React.ReactElement} The Result component
 */
const Result = ( { children, ...props } ) => {
    const classNames = classnames( {
        [ 'result-small' ]: props.size === 'small' ? true : false,
        [ 'result-hug' ]: props.hug ? true : false,
        [ props.className ]: props.className ? true : false
    } );
    let tempStatus = props.status;
    // antd won't let you overwrite the icon/image when it's one of these status's (?!?)
    // so we use the original status to get the correct icon/image
    // but overwrite the one we pass to the antd component
    if ( props.status === '404' || props.status === '403' || props.status === '500' ) {
        tempStatus = 'error';
    }
    return (
        <AntResult
            icon={ props.type === 'image' ? getImage( props.status, props.title ) : getIcon( props.status ) }
            { ...props }
            status={ tempStatus }
            className={ classNames }
        >
            { children }
        </AntResult>
    );
};

export { Result };

